import { useEffect, useState } from "react";
import { useNavigate, NavLink, Outlet, Link } from "react-router-dom";
import { Siren, LayoutDashboard, FolderKanban, MessageSquare, Quote, BarChart3, LogOut, ExternalLink, Wallet, FileText, Users, Trophy, Package, Bell, Newspaper, ShieldCheck, Languages, Image as ImageIcon, MessageCircle, Briefcase, BookOpen, Mail, GraduationCap } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import api from "../../lib/api";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, perm: "overview.view" },
  { to: "/admin/records", label: "Records (360°)", icon: Users, perm: "overview.view" },
  { to: "/admin/applications", label: "Applications", icon: FileText, perm: "applications.view" },
  { to: "/admin/emergency", label: "Emergency", icon: Siren, perm: "applications.view", badge: "emergency" },
  { to: "/admin/finance", label: "Finance", icon: Wallet, perm: "finance.view" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, perm: ["research.view", "me.view", "finance.view"] },
  { to: "/admin/registry", label: "Registry", icon: FileText, perm: ["partnerships.view", "content.view", "community_needs.view", "safeguarding.view", "complaints.view"] },
  { to: "/admin/volunteers", label: "Volunteers", icon: FileText, perm: "volunteers.view" },
  { to: "/admin/training", label: "Training & Certification", icon: GraduationCap, perm: "training.view" },
  { to: "/admin/employment", label: "Employment Bridge", icon: Briefcase, perm: "employment.view" },
  { to: "/admin/store", label: "Book Store", icon: BookOpen, perm: "store.view" },
  { to: "/admin/beneficiary-testimonials", label: "Beneficiary Testimonials", icon: Quote, perm: "content.view" },
  { to: "/admin/site-copy", label: "Site Content", icon: Languages, perm: "content.view" },
  { to: "/admin/campaigns", label: "Email campaigns", icon: Mail, perm: "content.view" },
  { to: "/admin/cards", label: "ID Cards", icon: Users, perm: "volunteers.view" },
  { to: "/admin/community", label: "Community Chat", icon: MessageCircle, perm: "volunteers.view" },
  { to: "/admin/beneficiaries", label: "Beneficiaries", icon: Users, perm: "beneficiaries.view" },
  { to: "/admin/donors", label: "Donors", icon: Wallet, perm: "finance.view" },
  { to: "/admin/selection", label: "Selection", icon: Trophy, perm: "selection.view" },
  { to: "/admin/family", label: "Family food", icon: Package, perm: "applications.view" },
  { to: "/admin/supported-projects", label: "Supported projects", icon: FolderKanban, perm: "finance.view" },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban, perm: "projects.view" },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote, perm: "content.view" },
  { to: "/admin/updates", label: "Updates & News", icon: Newspaper, perm: "content.view" },
  { to: "/admin/governance", label: "Governance docs", icon: ShieldCheck, perm: "content.view" },
  { to: "/admin/translations", label: "Translations", icon: Languages, perm: "content.view" },
  { to: "/admin/media", label: "Media library", icon: ImageIcon, perm: "content.view" },
  { to: "/admin/notifications", label: "SMS & WhatsApp", icon: MessageCircle, perm: "overview.view" },
  { to: "/admin/impact", label: "Impact stats", icon: BarChart3, perm: "me.view" },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, perm: "communications.view" },
  { to: "/admin/review", label: "Review & approvals", icon: FileText, perm: ["projects.approve", "content.approve", "applications.approve", "finance.approve", "partnerships.approve", "projects.edit", "content.edit"] },
  { to: "/admin/users", label: "Users & permissions", icon: Users, perm: "users.view" },
  { to: "/admin/audit", label: "Audit log", icon: FileText, perm: "audit.view" },
];

function NotificationsBar() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { const f = () => api.get("/admin/notifications").then((r) => setItems(r.data)).catch(() => {}); f(); const t = setInterval(f, 60000); return () => clearInterval(t); }, []);
  const unread = items.filter((n) => !n.read).length;
  return (
    <div className="flex justify-end mb-4 relative" data-testid="notifications-bar">
      <button onClick={() => { setOpen(!open); if (unread) api.put("/admin/notifications/read").then(() => setItems(items.map((n) => ({ ...n, read: true })))); }} className="relative inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-md bg-white border border-black/10" data-testid="notifications-toggle">
        <Bell size={14} /> Notifications {unread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] rounded-full px-1.5" data-testid="notifications-unread">{unread}</span>}
      </button>
      {open && <div className="absolute top-9 right-0 w-96 max-h-80 overflow-y-auto bg-white border border-black/10 rounded-xl shadow-xl z-30 p-2" data-testid="notifications-list">{items.length === 0 ? <div className="text-xs p-3 text-[#0a0d12]/50">No notifications yet.</div> : items.map((n) => <Link key={n.id} to={n.link || "/admin"} onClick={() => setOpen(false)} className="block text-xs p-2 rounded hover:bg-[#f8f4e8]"><div>{n.text}</div><div className="text-[10px] text-[#0a0d12]/50">{new Date(n.at).toLocaleString()}</div></Link>)}</div>}
    </div>
  );
}

export default function AdminLayout() {
  const [emgNew, setEmgNew] = useState(0);
  useEffect(() => { api.get("/admin/emergency?status=new").then((r) => setEmgNew(r.data.items.length)).catch(() => {}); }, []);
  const { user, logout, can } = useAuth();
  const nav = useNavigate();
  const visible = NAV.filter((n) => (Array.isArray(n.perm) ? n.perm.some(can) : can(n.perm)));
  const onLogout = async () => { await logout(); nav("/admin/login"); };
  useEffect(() => { if (user?.must_change_password) nav("/admin/change-password", { replace: true }); }, [user, nav]);

  return (
    <div className="min-h-screen bg-[#f4f6fa] flex" data-testid="admin-shell">
      <aside className="w-64 bg-[#155e3f] text-white flex-shrink-0 hidden md:flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <img src="/dk-logo.png" alt="Dutsen Kauna Foundation monogram" className="w-10 h-10 object-contain" />
            <div>
              <div className="font-serif font-bold text-base leading-tight">Dutsen Kauna</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#d6aa43]">Admin</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {visible.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition ${
                    isActive ? "bg-[#d6aa43] text-[#0a0d12]" : "text-white hover:bg-white/5 hover:text-white"
                  }`
                }
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={16} /> {n.label}{n.badge === "emergency" && emgNew > 0 && <span className="ml-auto bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="emg-nav-badge">{emgNew}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" target="_blank" className="flex items-center gap-2 text-xs text-white hover:text-white">
            <ExternalLink size={14} /> View site
          </Link>
          <div className="text-xs text-white">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#d6aa43]" data-testid="admin-role-label">{user?.role_label}</div>
          <Link to="/admin/account" className="text-xs text-white hover:text-white" data-testid="admin-account-link">My account</Link>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        {/* Mobile bar */}
        <header className="md:hidden bg-[#155e3f] text-white p-4 flex items-center justify-between">
          <div className="font-serif font-bold">Admin</div>
          <button onClick={onLogout} className="text-sm text-[#d6aa43]">Logout</button>
        </header>
        <main className="flex-1 p-6 lg:p-10">
          <NotificationsBar />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
